import React, { useEffect, useMemo, useState } from "react";

import { Button } from "../Button";
import Select from "../Select";
import { SingleValue }  from 'react-select';
import TextInput from "../TextInput";
import Result from "../Result";
import './rmd.css';
import { INITIAL_AGE, INITIAL_DISTRIBUTION_PERIOD } from './config';
import { parseNumber, formatNumber, postHeightToParent } from '../common-helpers';
import { getAgeValues, getDistributionPeriod, calc } from './helpers';

export const RMD: React.FC = () => {
  const [age, setAge] = useState<string>(INITIAL_AGE.toString());
  const [distributionPeriod, setDistributionPeriod] = useState<number>(INITIAL_DISTRIBUTION_PERIOD);
  const [hasBalanceError, setHasBalanceError] = useState(false);
  const [balance, setBalance] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [rmd, setRmd] = useState<string>('Result');

  const ageValues = getAgeValues();
  const options = useMemo(() => (
    ageValues.map(ageValue => ({
      label: ageValue,
      value: ageValue,
    }))
  ), [ageValues]);

  const defaultOption = useMemo(() => ([{ label: age, value: age }]), [age]);

  const onAgeChange = (option: SingleValue<{label: string, value: string} | null>) => {
    let newAge = option === null ? INITIAL_AGE.toString() : option.value;
    setAge(newAge);
    setDistributionPeriod(getDistributionPeriod(newAge));
  };

  const onBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const balanceValue: string = parseNumber(e.target.value).toString();

    if (balanceValue === '') {
      setHasBalanceError(true);
    } else if (hasBalanceError === true) {
      setHasBalanceError(false);
    }

    setBalance(balanceValue);
  };

  const validateForm = () => {
    if (balance === '') {
      setHasBalanceError(true);
      return false;
    }

    return true;
  };

  const onSubmit = () => {
    if (validateForm()) {
      const result = calc(distributionPeriod, balance);
      setShowResult(true);
      setRmd(result.toString());
    }
  };

  const onReset = () => {
    setShowResult(false);
    setRmd("");
    setAge(INITIAL_AGE.toString());
    setDistributionPeriod(INITIAL_DISTRIBUTION_PERIOD);
    setBalance('');
  }

  useEffect(() => {
    postHeightToParent(document.body.scrollHeight);
  }, [showResult]);

  return (
  	<div className="container__rmdCalc">
  		<form className="rmdCalc-form">
        {showResult ? (
          <>
          <h3 className="rmdCalc-result-title">Your 2022 Required Minimum Distribution:<br/>
            <Result
              id="rmd-result"
              classNames="rmdCalc-result-accent"
              result={`$ ` + formatNumber(rmd)}
            />
          </h3>
          <p className="rmdCalc-result-description">If you have more than one traditional IRA, you may take the distribution from any single account or any combination of accounts. The withdrawal must be made by December 31, unless you reach age 72 during 2022. In that case, the 2022 RMD can be taken as late as April 1, 2023.</p>
          </>
        ) : (
        <>
          <div className="inputGroup">
            <label htmlFor="rmdCalc__age" className="rmdCalc-label">Age at the end of this year:</label>
              <Select
                id="rmdCalc__age"
                name="age"
                classNames="rmdCalc-select"
                options={options}
                defaultOption={defaultOption}
                onChange={onAgeChange}
              />
          </div>
          <div className="inputGroup">
            <label htmlFor="rmdCalc__balance" className="rmdCalc-label">Balance of your IRAs at December 31 of last year:</label>
            <span className="rmdCalc-input-prefix">$</span>
            <TextInput
              id="rmdCalc_balance"
              name="balance"
              inputMode="numeric"
              fieldClass={hasBalanceError ? `rmdCalc-input rmdCalc-input_error` : `rmdCalc-input`}
              onChange={onBalanceChange}
              value={formatNumber(balance)}
              data-testid="rmdCalc-balance"
            />
            <span className="rmdCalc-error">{hasBalanceError && ("Please, enter correct number")}</span>
          </div>
        </>
        )}
        <div className="rmdCalc-actions">
          {showResult ? (
            <Button
              label="Start Over"
              className="primary-button rmdCalc-btn"
              onClickHandler={onReset}
            />
          ) : (
          <Button
            label="Submit"
            className="primary-button rmdCalc-btn"
            onClickHandler={onSubmit}
            data-testid="rmdCalc-calculate"
          />)}
        </div>
      </form>
    </div>
  );
}
